<template>
  <div ref="root" class="rd rd--contracts" :class="{ 'ui-compact': compact }" :data-w="layout" :data-density="density" :data-bar="barSticky ? 'sticky' : 'flow'" :style="rootStyle">
    <div class="rd-main">
      <report-nav :groups="menuGroups" :current="currentKey" :label="$t('contracts_title')" @select="openScreen">
        <template #tools>
          <button type="button" class="ui-icon-btn ui-icon-btn--sm ui-icon-btn--plain" :title="`${$t('report_desktop.shortcuts')} (?)`" :aria-label="$t('report_desktop.shortcuts')" :aria-expanded="String(helpOpen)" @click="helpOpen = !helpOpen">
            <v-icon size="18">mdi-keyboard-outline</v-icon>
          </button>
          <button type="button" class="ui-text-btn rd-nav__legacy" :title="$t('report_desktop.old_view')" @click="$emit('legacy')">
            <v-icon size="16" color="#c2410c">mdi-history</v-icon>
            <span class="rd-nav__legacy-label">{{ $t("report_desktop.old_view") }}</span>
          </button>
        </template>
      </report-nav>

      <div class="rd-page">
        <div class="rd-head">
          <div class="rd-head__title">
            <h1>{{ $t("contracts_title") }}</h1>
            <p class="rd-head__sub">{{ $t("contracts_page_subtitle") }}</p>
          </div>
          <div class="rd-head__right">
            <button type="button" class="ui-icon-btn ui-icon-btn--sm" :title="`${$t('refresh')} (r)`" :aria-label="$t('refresh')" @click="reload">
              <v-icon size="18">mdi-refresh</v-icon>
            </button>
            <button type="button" class="ui-icon-btn ui-icon-btn--sm" :title="$t('print')" :aria-label="$t('print')" :disabled="!printable" @click="printPage">
              <v-icon size="18">mdi-printer-outline</v-icon>
            </button>
            <ui-button variant="primary" icon="mdi-plus" aria-keyshortcuts="n" @click="pickDialog = true">{{ compact ? $t("contracts_shell.new_short") : $t("new_contract") }}</ui-button>
          </div>
        </div>
      </div>

      <ui-filter-bar v-if="tabStatus === 'ready' && allRows.length" ref="bar" :segments="segments" :query="query" :placeholder="$t('contracts_search_placeholder')" :search-label="$t('search')" :count="$t('contracts_shell.count', { shown: rows.length, total: allRows.length })" @segment="(id, value) => setFilter(value)" @query="query = $event" />

      <div class="rd-page ui-screen">
        <ui-state v-if="tabStatus === 'loading'" kind="loading" />
        <ui-state v-else-if="tabStatus === 'error'" kind="error" icon="mdi-alert-outline" :title="$t('contracts_shell.state.error_title')" :text="$t('contracts_shell.state.error_text')">
          <ui-button variant="dark" icon="mdi-refresh" @click="reload">{{ $t("contracts_shell.state.retry") }}</ui-button>
        </ui-state>
        <ui-state v-else-if="!allRows.length" kind="empty" :icon="currentKey === 'pending' ? 'mdi-cash-check' : 'mdi-file-document-outline'" :title="$t(`contracts_shell.state.empty_${currentKey}_title`)" :text="$t(`contracts_shell.state.empty_${currentKey}_text`)" />
        <template v-else>
          <ui-kpis :tiles="kpiTiles" :label="$t('contracts_shell.kpi.label')" @pick="pickKpi" />
          <ui-state v-if="!rows.length" kind="empty" icon="mdi-magnify" :title="$t('contracts_shell.state.filtered_title')" :text="$t('contracts_shell.state.filtered_text')">
            <ui-button @click="resetFilters">{{ $t("contracts_shell.state.reset") }}</ui-button>
          </ui-state>
          <contract-list v-else-if="currentKey === 'overview'" :rows="rows" :active-id="activeId" @open="openRestaurantRow" @renew="renewRow" @create="renewRow" />
          <pending-list v-else :rows="rows" @open="openPending" />
        </template>
      </div>
    </div>

    <report-detail-panel :stack="panelStack" @close="closePanel" @back="panelBack" @open="openPanel" @toast="onPanelToast" />
    <transition name="rd-fade">
      <div v-if="panelStack.length" class="rd-scrim" @click="closePanel"></div>
    </transition>

    <report-help-popover :open="helpOpen" :shortcuts="shortcuts" @close="helpOpen = false" />
    <ui-toast :toast="toast" @action="runToastAction" />

    <pick-restaurant-dialog v-model="pickDialog" @confirm="openNewContract" />
    <contract-form-dialog v-model="formDialog" :restaurant="target" @saved="refreshAll" />
    <contract-edit-dialog v-model="editDialog" :restaurant-id="target.id || 0" :contract="editContract" @saved="refreshAll" />
    <restaurant-services-dialog v-model="servicesDialog" :restaurant="target" />
  </div>
</template>

<script>
import "@/components/report/desktop/reportDesktop.scss";
import "./contractsShell.scss";
import ReportNav from "@/components/report/shared/ReportNav.vue";
import ReportHelpPopover from "@/components/report/shared/ReportHelpPopover.vue";
import ReportDetailPanel from "@/components/report/desktop/ReportDetailPanel.vue";
import hasReportShell from "@/components/report/shared/hasReportShell";
import hasPrinting from "@/mixins/hasPrinting";
import { escapeHtml } from "@/utils/escapeHtml";
import PickRestaurantDialog from "@/components/contracts/PickRestaurantDialog.vue";
import ContractFormDialog from "@/components/contracts/ContractFormDialog.vue";
import ContractEditDialog from "@/components/contracts/ContractEditDialog.vue";
import RestaurantServicesDialog from "@/components/restaurants/RestaurantServicesDialog.vue";
import contractsService from "@/services/contracts";
import pricingService from "@/services/pricing";
import hasContractLabels from "./hasContractLabels";
import ContractList from "./parts/ContractList.vue";
import PendingList from "./parts/PendingList.vue";
import RestaurantPanel from "./panels/RestaurantPanel.vue";
import { CONTRACTS_QUERY, CONTRACT_TABS, DEFAULT_TAB, OVERVIEW_FILTERS, PENDING_FILTERS } from "./contractsQuery";
import {
  buildOverviewRows,
  buildPendingRows,
  countOverview,
  countPending,
  filterOverview,
  filterPending,
  pendingOpensContracts,
} from "./contractsLogic";

const COMPACT_WIDTH = 760;
const TAB_ICONS = { overview: "mdi-file-document-multiple-outline", pending: "mdi-cash-check" };
/** grupe radnog spiska koje traže radnju (sve osim "OK"): zbir ide u značku taba */
const PENDING_ATTENTION = ["missing", "no_amount", "unverified", "contract_expired", "contract_expiring"];
const PENDING_WARN = ["missing", "no_amount", "contract_expired", "contract_expiring"];

/**
 * Ugovori (/ugovori, samo admin). Isti izgled kao izvještaji, finansije i firme za dostavu: gornja navigacija (Pregled ugovora,
 * Čekaju potvrdu cijene), brojke koje filtriraju, traka filtera, lista (tabela ili kartice) i istorija ugovora restorana u desnom panelu.
 * Forme (novi ugovor, izmjena, usluge i cijene) su postojeći dijalozi koje ova ljuska hostuje; kad se sačuvaju, liste se ponovo učitaju.
 * Stanje u adresi: ?t=pending&s=filter; ?ui=stari vraća staru stranicu.
 */
export default {
  name: "ContractsShell",
  mixins: [hasReportShell, hasContractLabels, hasPrinting],
  components: {
    ReportNav,
    ReportHelpPopover,
    ReportDetailPanel,
    ContractList,
    PendingList,
    PickRestaurantDialog,
    ContractFormDialog,
    ContractEditDialog,
    RestaurantServicesDialog,
  },
  data: () => ({
    overviewStatus: "loading",
    pendingStatus: "loading",
    contractsRaw: [],
    withoutContract: [],
    pendingRaw: [],
    query: "",
    /** raste kad se nešto sačuva: panel restorana ponovo učitava istoriju */
    version: 0,
    target: {},
    editContract: {},
    pickDialog: false,
    formDialog: false,
    editDialog: false,
    servicesDialog: false,
  }),
  computed: {
    phone() {
      return Boolean(this.device && this.device.isMobile);
    },
    /** kartice umjesto tabele i na tabletu: ispod 760 px širine stranice tabela ne stane sa dugmetom u redu */
    compact() {
      return this.phone || this.width < COMPACT_WIDTH;
    },
    menuKeys() {
      return CONTRACT_TABS;
    },
    currentKey() {
      const wanted = this.$route.query[CONTRACTS_QUERY.tab];
      return CONTRACT_TABS.includes(wanted) ? wanted : DEFAULT_TAB;
    },
    filterKey() {
      const wanted = this.$route.query[CONTRACTS_QUERY.status];
      const allowed = this.currentKey === "overview" ? OVERVIEW_FILTERS : PENDING_FILTERS;
      return allowed.includes(wanted) ? wanted : "all";
    },
    tabStatus() {
      return this.currentKey === "overview" ? this.overviewStatus : this.pendingStatus;
    },
    overviewRows() {
      return buildOverviewRows(this.contractsRaw, this.withoutContract);
    },
    overviewCounts() {
      return countOverview(this.overviewRows);
    },
    pendingRows() {
      return buildPendingRows(this.pendingRaw);
    },
    pendingCounts() {
      return countPending(this.pendingRows);
    },
    /** svi redovi aktivnog taba (prije filtera) */
    allRows() {
      return this.currentKey === "overview" ? this.overviewRows : this.pendingRows;
    },
    rows() {
      const byFilter = this.currentKey === "overview" ? filterOverview(this.overviewRows, this.filterKey) : filterPending(this.pendingRows, this.filterKey);
      const fields = this.currentKey === "overview" ? [{ value: "name" }, { value: "contractNumber" }] : [{ value: "name" }, { value: "contractNumber" }, { value: "status" }];
      return this.customSearch(byFilter, fields, this.query);
    },
    /** brojke na vrhu: pregled ima 4 stanja ugovora, radni spisak grupe koje imaju bar jedan red */
    kpiTiles() {
      const key = this.filterKey;
      if (this.currentKey === "overview") {
        const c = this.overviewCounts;
        return [
          { key: "expired", label: this.$t("status_expired"), value: c.expired, on: key === "expired", warn: c.expired > 0 },
          { key: "expiring", label: this.$t("status_expiring_soon"), value: c.expiring, on: key === "expiring", warn: c.expiring > 0 },
          { key: "valid", label: this.$t("status_active_count"), value: c.valid, on: key === "valid" },
          { key: "none", label: this.$t("contracts_shell.kpi.none"), value: c.none, on: key === "none" },
        ];
      }
      const c = this.pendingCounts;
      return PENDING_FILTERS.filter((group) => c[group] > 0 || key === group).map((group) => ({
        key: group,
        label: this.$t(`contracts_shell.pending.${group}`),
        value: c[group] || 0,
        on: key === group,
        warn: PENDING_WARN.includes(group) && c[group] > 0,
      }));
    },
    segments() {
      const options = [{ value: "all", label: this.$t("all_statuses") }].concat(this.kpiTiles.map((tile) => ({ value: tile.key, label: tile.label })));
      return [{ id: "status", label: this.$t("status"), value: this.filterKey, options }];
    },
    menuGroups() {
      const attention = PENDING_ATTENTION.reduce((sum, group) => sum + (this.pendingCounts[group] || 0), 0);
      const late = this.overviewCounts.expired + this.overviewCounts.expiring;
      const items = [
        { key: "overview", icon: TAB_ICONS.overview, title: this.$t("contracts_overview"), badge: late || "" },
        { key: "pending", icon: TAB_ICONS.pending, title: this.$t("pending_price_verification"), badge: attention || "" },
      ];
      return [{ key: "contracts", title: this.$t("contracts_shell.group"), items }];
    },
    activeId() {
      const top = this.panelStack[this.panelStack.length - 1];
      return top && top.props ? top.props.restaurantId || 0 : 0;
    },
    printable() {
      return this.tabStatus === "ready" && this.rows.length > 0;
    },
    shortcuts() {
      return [
        { keys: "n", label: this.$t("contracts_shell.keys.new") },
        { keys: "1  2", label: this.$t("contracts_shell.keys.tabs") },
        { keys: "/", label: this.$t("contracts_shell.keys.search") },
        { keys: "r", label: this.$t("contracts_shell.keys.refresh") },
        { keys: "Esc", label: this.$t("report_desktop.keys_close") },
        { keys: "?", label: this.$t("report_desktop.shortcuts") },
      ];
    },
  },
  watch: {
    currentKey() {
      this.closePanel();
      this.query = "";
      window.scrollTo(0, 0);
    },
    // posle zatvaranja usluga i cijena spisak se osvježava: cijena je mogla biti potvrđena ili unesena
    servicesDialog(open) {
      if (!open) this.refreshAll();
    },
  },
  created() {
    this.loadOverview();
    this.loadPending();
  },
  methods: {
    // hasReportShell zove ove kad korisnik pritisne r, [ ili ] i broj
    reload() {
      this.refreshAll({ visible: true });
    },
    shiftDay() {
      // lista nema dane
    },
    openScreen(key) {
      this.setQuery({ [CONTRACTS_QUERY.tab]: key === DEFAULT_TAB ? undefined : key, [CONTRACTS_QUERY.status]: undefined });
    },
    // ------------------------------------------------------------ podaci
    refreshAll({ visible = false } = {}) {
      this.version += 1;
      this.loadOverview({ silent: !visible && this.overviewStatus === "ready" });
      this.loadPending({ silent: !visible && this.pendingStatus === "ready" });
    },
    async loadOverview({ silent = false } = {}) {
      if (!silent) this.overviewStatus = "loading";
      try {
        const { data } = await contractsService.getExpiringContracts();
        this.contractsRaw = (data && data.contracts) || [];
        this.withoutContract = (data && data.bez_ugovora) || [];
        this.overviewStatus = "ready";
      } catch (error) {
        if (silent) this.showToast(this.$t("error_loading_contracts"));
        else this.overviewStatus = "error";
      }
    },
    async loadPending({ silent = false } = {}) {
      if (!silent) this.pendingStatus = "loading";
      try {
        const { data } = await pricingService.getPendingPriceVerification();
        this.pendingRaw = (data && data.items) || [];
        this.pendingStatus = "ready";
      } catch (error) {
        if (silent) this.showToast(this.$t("error_loading_pending_verification"));
        else this.pendingStatus = "error";
      }
    },
    // ------------------------------------------------------------ filteri
    setQuery(patch) {
      const query = { ...this.$route.query, ...patch };
      Object.keys(query).forEach((key) => (query[key] === undefined || query[key] === "all") && delete query[key]);
      this.$router.replace({ query }).catch(() => {});
    },
    setFilter(value) {
      this.setQuery({ [CONTRACTS_QUERY.status]: value === "all" ? undefined : value });
    },
    /** klik na brojku bira filter, a drugi klik na istu ga skida */
    pickKpi(key) {
      this.query = "";
      this.setFilter(this.filterKey === key ? "all" : key);
    },
    resetFilters() {
      this.query = "";
      this.setFilter("all");
    },
    // ------------------------------------------------------------ panel i dijalozi
    openRestaurantRow(row) {
      this.openRestaurant(row.restaurantId, row.name);
    },
    openRestaurant(id, name) {
      this.openPanel({
        key: `restaurant-${id}`,
        title: this.restaurantLabel(name),
        subtitle: `#${id}`,
        component: RestaurantPanel,
        props: { restaurantId: Number(id), name: name || "" },
        root: true,
      });
    },
    /** dijalozi dobijaju naziv u pismu izabranog jezika (naslov dijaloga) */
    setTarget(restaurant) {
      this.target = { id: restaurant.id, name: this.restaurantLabel(restaurant.name) };
    },
    renewRow(row) {
      this.openNewContract({ id: row.restaurantId, name: row.name });
    },
    openNewContract(restaurant) {
      this.setTarget(restaurant);
      this.formDialog = true;
    },
    openEditContract(restaurant, contract) {
      this.setTarget(restaurant);
      this.editContract = contract;
      this.editDialog = true;
    },
    openServices(restaurant) {
      this.setTarget(restaurant);
      this.servicesDialog = true;
    },
    /** radni spisak: problem ugovora otvara istoriju ugovora, problem cjenovnika otvara usluge i cijene */
    openPending(row) {
      if (pendingOpensContracts(row.group)) this.openRestaurant(row.restaurantId, row.name);
      else this.openServices({ id: row.restaurantId, name: row.name });
    },
    onPanelToast(text, options) {
      this.showToast(text, options || {});
    },
    // ------------------------------------------------------------ štampa
    /** Štampa spiska aktivnog taba kao u izvještajima: poseban prozor samo sa tabelom (redovi kakvi su na ekranu, sa filterima). */
    printPage() {
      const dash = "—";
      const overview = this.currentKey === "overview";
      const columns = (overview
        ? [
            { text: this.$t("restaurant"), value: "name" },
            { text: this.$t("status"), value: "status" },
            { text: this.$t("contract_number"), value: "contract" },
            { text: this.$t("contracts_shell.col.period"), value: "period" },
            { text: this.$t("contracts_shell.col.services"), value: "services", align: "right" },
          ]
        : [
            { text: this.$t("restaurant"), value: "name" },
            { text: this.$t("type"), value: "type" },
            { text: this.$t("status"), value: "status" },
            { text: this.$t("price"), value: "price" },
            { text: this.$t("price_valid_from"), value: "from" },
            { text: this.$t("contract_number"), value: "contract" },
          ]
      ).map((column) => ({ ...column, text: escapeHtml(column.text) }));
      const items = this.rows.map((row) => {
        let cells;
        if (overview) {
          cells = {
            name: `${this.restaurantLabel(row.name)} (#${row.restaurantId})`,
            status: this.statusText(row),
            contract: row.contractNumber || dash,
            period: this.periodText(row.from, row.to) || dash,
            services: row.services === null ? dash : row.services,
          };
        } else {
          const price = this.priceParts(row);
          cells = {
            name: `${this.restaurantLabel(row.name)} (#${row.restaurantId})`,
            type: this.typeLabel(row.kind),
            status: this.pendingText(row),
            price: [price.amount, price.label].filter(Boolean).join(" · ") || dash,
            from: row.validFrom ? this.localeDate(row.validFrom) : dash,
            contract: row.contractNumber || dash,
          };
        }
        return Object.fromEntries(Object.entries(cells).map(([key, value]) => [key, escapeHtml(value)]));
      });
      const filter = this.segments[0].options.find((option) => option.value === this.filterKey);
      const subtitle = [this.filterKey !== "all" && filter ? filter.label : "", this.query.trim() ? `"${this.query.trim()}"` : ""].filter(Boolean).join(" · ");
      const title = overview ? this.$t("contracts_overview") : this.$t("pending_price_verification");
      this.printDocument({
        pageTitle: `${this.$t("contracts_title")} - ${title}`,
        title: escapeHtml(`${this.$t("contracts_title")}: ${title}`),
        subtitle: escapeHtml(subtitle),
        tables: [{ headers: columns, items }],
      });
    },
    onKeydown(event) {
      const target = event.target;
      const typing = target && (["INPUT", "SELECT", "TEXTAREA"].includes(target.tagName) || target.isContentEditable);
      if (event.key === "n" && !typing && !event.ctrlKey && !event.metaKey && !event.altKey && !document.querySelector(".v-dialog--active")) {
        event.preventDefault();
        this.pickDialog = true;
        return;
      }
      hasReportShell.methods.onKeydown.call(this, event);
    },
  },
};
</script>
